import { useState, useEffect } from "react";
import { message } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { AttendenceStatusLabel } from "../../../Constants/Enums/Attendance/AttendanceStatusEnum";
import { Navigate, useNavigate } from "react-router-dom";

function EmployeeAttendanceService(userId) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [status, setStatus] = useState("Unknown");
  const [workingHours, setWorkingHours] = useState({});
  const [othersInfo, setOthersInfo] = useState({});
  const [requiredHrsMet, setRequiredHrsMet] = useState("No");
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  // UI Modals
  const [checkIn, setCheckIn] = useState(false);
  const [checkOut, setCheckOut] = useState(false);
  const [history, setHistory] = useState(false);

  const formatTo12Hour = (timeString) => {
    if (!timeString) return "N/A";

    try {
      // Handle different time string formats
      let timePart = timeString;

      // If it's in format "HH:MM:SS" or "HH:MM"
      if (timeString.includes(":")) {
        timePart = timeString.split(" ")[0]; // Remove timezone if present
      }

      const [hours, minutes] = timePart.split(":");
      const hour = parseInt(hours);
      const minute = minutes || "00";

      const period = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12; // Convert 0, 13-23 to 12, 1-11

      return `${formattedHour}:${minute} ${period}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return timeString; // Return original if formatting fails
    }
  };

  useEffect(() => {
    fetchAttendanceRecords();
  }, [userId]);

  // === FETCH ATTENDANCE DATA ===
  const fetchAttendanceRecords = async () => {
    try {
      const response = await axiosInstance.get(API.attendance.getAttendence);
      const data = response.data;
      setUserInfo({
        fullName: data.employee?.fullName || "",
        email: data.employee?.email || "",
      });

      setStatus(AttendenceStatusLabel[data.status] || "Unknown");

      if (data.workingHours) {
        setWorkingHours(data.workingHours);
      }
      setOthersInfo({
        totalWorkedHours: data.totalWorkedHours || 0,
        totalWindows: data.totalWindows || 0,
        isLate: data.isLate,
        isEarlyLeave: data.isEarlyLeave,
        canCheckIn: data.canCheckIn,
        canCheckOut: data.canCheckOut,
        blockReason: data.blockReason,
        overtimeHours: data.overtimeHours,
        remarks: data.remarks,
        isLate: data.isLate,
        logs: data.logs || [],
      });

      setAttendanceRecords(data.logs || []);

      const isHoursMet =
        data.workingHours?.fullDayRequiredHours &&
        data.totalWorkedHours >= data.workingHours.fullDayRequiredHours
          ? "Yes"
          : "No";
      setRequiredHrsMet(isHoursMet);
    } catch (error) {
      console.error(" Error fetching attendance:", error);
      message.error("Failed to load attendance records");
    }
  };

  // In EmployeeAttendanceService.js
  const getDateDetail = async (attendanceId) => {
    try {
      const response = await axiosInstance.get(API.attendance.getDateDetail(attendanceId));
      return response;
    } catch (error) {
      console.error("❌ Error fetching date detail:", error);
      throw error;
    }
  };

  // === CHECK-IN ===
  const handleAddCheckIn = async () => {
    try {
      setIsLoading(true);
      const payload = {
        isManual: false,
      };

      const response = await axiosInstance.post(API.attendance.addCheckIn, payload);
      message.success("Check-in successful!");
      setCheckIn(false);
      fetchAttendanceRecords();
    } catch (error) {
      console.error(" Check-in failed:", error);
      message.error(error.response?.data?.message || "Check-in failed");
    } finally {
      setIsLoading(false);
    }
  };

  // === CHECK-OUT ===
  const handleAddCheckOut = async () => {
    try {
      setIsLoading(true);
      const payload = {
        isManual: false,
        //  checkOutDateTime: getLocalDateTime(),
      };

      const response = await axiosInstance.post(API.attendance.addCheckOut, payload);
      message.success("Check-out successful!");
      setCheckOut(false);
      fetchAttendanceRecords();
    } catch (error) {
      console.error(" Check-out fsailed:", error);
      message.error(error.response?.data?.message || "Check-out failed");
    } finally {
      setIsLoading(false);
    }
  };

  // === HISTORY ===
  const handleHistory = () => {
    navigate("/employeedashboard/attendance/viewdetail");
  };

  return {
    userInfo,
    status,
    workingHours,
    othersInfo,
    requiredHrsMet,
    isLoading,
    isLoadingPage,
    checkIn,
    checkOut,
    history,
    attendanceRecords,
    handleAddCheckIn,
    handleAddCheckOut,
    handleHistory,
    setCheckIn,
    setCheckOut,
    setHistory,
    formatTo12Hour,
    getDateDetail,
  };
}

export default EmployeeAttendanceService;
